/**
 * features/setup/StudentsSetup.tsx
 * ------------------------------------------------------------------
 * Student CRUD. FEATURE #5: parent/guardian contact block
 * (email + WhatsApp + consent) is captured here and later powers
 * the per-class bulk report delivery (feature #6, Phase 4).
 * ------------------------------------------------------------------
 */
import { useState, useMemo } from 'react';
import {
  collection,
  deleteDoc,
  doc,
} from 'firebase/firestore';
// Batch A: undefined-safe write + save modal
import { addDoc, updateDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Mail,
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  UserSquare,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  sanitizeText,
  sanitizeEmail,
  sanitizePhone,
} from '@/lib/sanitize';
import { Button, Card, PageHeader, Spinner } from '@/components/ui/primitives';
import { FormModal } from '@/components/ui/glass';
import type { Student } from '@/types';
import StudentAvatar from '@/components/ui/StudentAvatar';

interface FormState {
  fullName: string;
  currentClass: string;
  gender: 'M' | 'F' | ''; // '' = not yet chosen (placeholder); required before save
  admissionNumber: string;
  // parent contact (#5)
  guardianName: string;
  parentEmail: string;
  parentWhatsapp: string;
  consentToSend: boolean;
}

const EMPTY: FormState = {
  fullName: '',
  currentClass: '',
  gender: 'M',
  admissionNumber: '',
  guardianName: '',
  parentEmail: '',
  parentWhatsapp: '',
  consentToSend: false,
};

export default function StudentsSetup() {
  const { students: allStudents, classes, loading } =
    useData();
  // Exclude 'pending' (Pastel-only, not yet enrolled) from the roster + counts.
  const students = allStudents.filter((s) => s.status !== 'pending');
  const { userData } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editing, _setEditing] = useState<Student | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Student | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return [...students]
      .filter(
        (s) =>
          (!classFilter || s.currentClass === classFilter) &&
          (s.fullName.toLowerCase().includes(q) ||
            s.admissionNumber?.toLowerCase().includes(q)),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [students, search, classFilter]);

  const className = (id: string) =>
    classes.find((c) => c.id === id)?.name ?? '—';

  // Contact completeness stats for the header (useful before bulk send)
  const contactStats = useMemo(() => {
    const withEmail = students.filter((s) => s.parentContact?.email).length;
    const withWa = students.filter((s) => s.parentContact?.whatsapp).length;
    return { withEmail, withWa, total: students.length };
  }, [students]);


  async function save() {
    const fullName = sanitizeText(form.fullName, 80);
    if (!fullName) {
      setMsg({ ok: false, text: 'Student name is required.' });
      return;
    }
    if (!form.currentClass) {
      setMsg({ ok: false, text: 'Please assign a class.' });
      return;
    }
    const email = sanitizeEmail(form.parentEmail);
    if (form.parentEmail && !email) {
      setMsg({ ok: false, text: 'Parent email looks invalid.' });
      return;
    }
    setBusy(true);
    setSaveModal({
      show: true,
      status: 'saving',
      message: editing ? 'Updating student…' : 'Adding student…',
    });
    try {
      // Subjects & their teachers are no longer set at admission — they
      // are managed on the pupil's profile (Subjects tab). New pupils
      // start with none; editing here preserves whatever is already set.
      const payload = {
        fullName,
        currentClass: form.currentClass,
        gender: form.gender,
        admissionNumber: sanitizeText(form.admissionNumber, 30),
        subjects: editing?.subjects ?? [],
        subjectTeachers: editing?.subjectTeachers ?? {},
        parentContact: {
          guardianName: sanitizeText(form.guardianName, 80),
          email,
          whatsapp: sanitizePhone(form.parentWhatsapp),
          consentToSend: form.consentToSend,
        },
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid,
      };
      if (editing) {
        await updateDoc(doc(db, COLLECTIONS.students, editing.id), payload);
        setSaveModal({ show: true, status: 'success', message: 'Student updated.' });
      } else {
        await addDoc(collection(db, COLLECTIONS.students), {
          ...payload,
          createdAt: new Date().toISOString(),
          createdBy: userData?.uid,
        });
        setSaveModal({ show: true, status: 'success', message: 'Student added.' });
      }
      setShowModal(false);
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function remove(s: Student) {
    setBusy(true);
    try {
      await deleteDoc(doc(db, COLLECTIONS.students, s.id));
      setMsg({ ok: true, text: `Removed ${s.fullName}.` });
      setConfirmDelete(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner label="Loading students…" />;

  return (
    <div>
      <PageHeader
        title="Students"
        subtitle={`${students.length} students · ${contactStats.withEmail} with parent email · ${contactStats.withWa} with WhatsApp`}
        actions={
          <a
            href="#/admissions"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primaryDark transition-colors"
          >
            <Plus size={16} />
            Add student
          </a>
        }
      />

      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search students…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
            />
          </div>
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            className="text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 px-3 py-2"
          >
            <option value="">All classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-12">
            No students found.
          </p>
        ) : (
          <div className="p-3 sm:p-4 space-y-2.5">
            {filtered.map((s) => {
              const pc = s.parentContact;
              const subjCount = s.subjects?.length ?? 0;
              return (
                <div
                  key={s.id}
                  className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all"
                >
                  <div className="flex items-center gap-4 p-4">
                    <StudentAvatar student={s} size="md" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-800 dark:text-white truncate">
                        {s.fullName}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                          {className(s.currentClass)}
                        </span>
                        <span>
                          Adm: {s.admissionNumber || '—'}
                        </span>
                        {subjCount > 0 && (
                          <span>
                            {subjCount} subject
                            {subjCount === 1 ? '' : 's'}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                          <Mail
                            size={13}
                            className={
                              pc?.email
                                ? 'text-emerald-600'
                                : 'text-gray-300 dark:text-gray-600'
                            }
                          />
                          <MessageCircle
                            size={13}
                            className={
                              pc?.whatsapp
                                ? 'text-emerald-600'
                                : 'text-gray-300 dark:text-gray-600'
                            }
                          />
                          {pc?.consentToSend && (
                            <CheckCircle2
                              size={13}
                              className="text-blue-500"
                            />
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <a
                        href={`#/studentProfile/${s.id}`}
                        className="p-2 rounded-lg hover:bg-primary/10 text-primary"
                        title="View profile"
                      >
                        <UserSquare size={16} />
                      </a>
                      <a
                        href={`#/admissions/${s.id}`}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                        title="Edit in Admissions"
                      >
                        <Pencil size={16} />
                      </a>
                      <button
                        onClick={() => setConfirmDelete(s)}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <FormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        icon={UserSquare}
        title={editing ? 'Edit student' : 'Add student'}
        maxWidth="max-w-lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              {editing ? 'Save changes' : 'Add student'}
            </Button>
          </>
        }
      >
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Full name</label>
                <input
                  value={form.fullName}
                  onChange={(e) =>
                    setForm({ ...form, fullName: e.target.value })
                  }
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium">Class</label>
                  <select
                    value={form.currentClass}
                    onChange={(e) =>
                      setForm({ ...form, currentClass: e.target.value })
                    }
                    className="w-full mt-1 px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <option value="">— select —</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Gender</label>
                  <select
                    value={form.gender}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        gender: e.target.value as 'M' | 'F' | '',
                      })
                    }
                    className="w-full mt-1 px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <option value="">— Select —</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Adm. No.</label>
                  <input
                    value={form.admissionNumber}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        admissionNumber: e.target.value,
                      })
                    }
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                  />
                </div>
              </div>

              {/* Parent / guardian contact — feature #5 */}
              <div className="rounded-xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-3 mt-2">
                <p className="text-xs font-bold uppercase tracking-wide text-primary dark:text-primaryLight mb-2">
                  Parent / Guardian contact (for report delivery)
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">
                      Guardian name
                    </label>
                    <input
                      value={form.guardianName}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          guardianName: e.target.value,
                        })
                      }
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium flex items-center gap-1">
                        <Mail size={13} /> Email
                      </label>
                      <input
                        value={form.parentEmail}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            parentEmail: e.target.value,
                          })
                        }
                        placeholder="parent@example.com"
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium flex items-center gap-1">
                        <MessageCircle size={13} /> WhatsApp
                      </label>
                      <input
                        value={form.parentWhatsapp}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            parentWhatsapp: e.target.value,
                          })
                        }
                        placeholder="+2677XXXXXXX"
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.consentToSend}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          consentToSend: e.target.checked,
                        })
                      }
                    />
                    Parent has consented to electronic report delivery
                  </label>
                  <p className="flex items-start gap-1 text-[11px] text-gray-500">
                    <AlertCircle size={12} className="mt-0.5 shrink-0" />
                    WhatsApp sending is stubbed for now; email delivery is
                    built in Phase 4. Numbers should include the country
                    code (+267…).
                  </p>
                </div>
              </div>

              <p className="flex items-start gap-1.5 text-[11px] text-gray-500 rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2">
                <AlertCircle size={12} className="mt-0.5 shrink-0" />
                Subjects and their teachers are set on the pupil's profile
                (Subjects tab) once the pupil has a class — not here.
              </p>
            </div>
      </FormModal>


      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card glass className="w-full max-w-sm p-6 animate-fade-in">
            <h2 className="text-lg font-bold">Remove student?</h2>
            <p className="text-sm text-gray-500 mt-2">
              Remove <strong>{confirmDelete.fullName}</strong>? This cannot
              be undone. Their saved marks remain in past records.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={busy}
                onClick={() => remove(confirmDelete)}
              >
                Remove
              </Button>
            </div>
          </Card>
        </div>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
